import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { DataType, RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { User } from '@vendasta/iam';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components/automation-replacement-chips/automation-replacement-chips.component';
import { SalespersonSelectorService } from '../../../../../data-services/salesperson-selector-service';
import { UsersService } from '../../../../../data-services/users.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';

@Component({
  selector: 'automata-assign-salesperson-panel',
  templateUrl: './assign-salesperson-panel.component.html',
  styleUrls: ['./assign-salesperson-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationReplacementChipsComponent,
    MatRadioModule,
  ],
})
export class AssignSalespersonPanelComponent extends CommonActionPanelComponent implements OnInit {
  supportedDataTypes: DataType[] = [DataType.DATA_TYPE_SMB_USER_ID, DataType.DATA_TYPE_STRING];
  salespersonSelectorService = this.injector.get(SalespersonSelectorService);
  form: UntypedFormGroup;
  salespersonFormControl: UntypedFormControl = new UntypedFormControl([]);
  shouldReassignFormControl: UntypedFormControl = new UntypedFormControl(false);
  dontShowSalespersonSelector$: Observable<boolean> = this.automationService
    .isNonPartnerAutomation()
    .pipe(map((is) => !!is));

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly usersService: UsersService,
    protected readonly fb: UntypedFormBuilder,
    private injector: Injector,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.salespersonFormControl.setValue(jsonData.user_ids || []);
    this.shouldReassignFormControl.setValue(jsonData.should_reassign || false);
    this.form = this.fb.group({
      userIds: this.salespersonFormControl,
      shouldReassign: this.shouldReassignFormControl,
    });
  }

  getUserDisplay(users: User[], userId: string): string {
    return this.usersService.getUserDisplay(users, userId);
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }
    jsonData.user_ids = this.form.controls.userIds.value;
    jsonData.should_reassign = this.form.controls.shouldReassign.value;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  finishedLoadingSelector(): void {
    this.savingEnabled = true;
  }
}
